/**
 * Locale-aware data-protection wording for printed / PDF exports.
 * Uses the browser locale when no explicit hint is provided so notices stay jurisdiction-aware
 * without requiring a company country field.
 */

const GDPR_STYLE_REGIONS = new Set([
  "AT", "BE", "BG", "HR", "CY", "CZ", "DK", "EE", "FI", "FR", "DE", "GR", "HU", "IE", "IT",
  "LV", "LT", "LU", "MT", "NL", "PL", "PT", "RO", "SK", "SI", "ES", "SE", "IS", "LI", "NO",
]);

export type DataProtectionNotice = { title: string; law: string; body: string };

/** Name of the governing privacy law for the caller's locale, phrased to slot into a sentence. */
export function resolveDataProtectionLaw(localeHint?: string | null): string {
  const raw =
    (localeHint && String(localeHint).trim()) ||
    (typeof navigator !== "undefined"
      ? navigator.language || (navigator as Navigator & { userLanguage?: string }).userLanguage
      : "") ||
    "en-GB";
  const locale = String(raw).replace(/_/g, "-");
  const region = (locale.split("-")[1] || "").toUpperCase();

  if (region === "GB" || region === "UK" || /^en-GB$/i.test(locale)) {
    return "UK GDPR and the Data Protection Act 2018";
  }
  if (region === "EU" || GDPR_STYLE_REGIONS.has(region)) {
    return "the EU General Data Protection Regulation (GDPR)";
  }
  if (region === "CH") return "the Swiss Federal Act on Data Protection (FADP)";
  if (region === "US") return "applicable US federal and state privacy laws";
  if (region === "CA") return "applicable Canadian privacy legislation, including PIPEDA where relevant";
  if (region === "AU") return "the Australian Privacy Act 1988";
  if (region === "NZ") return "the New Zealand Privacy Act 2020";
  if (region === "SG") return "Singapore’s Personal Data Protection Act (PDPA)";
  if (region === "ZA") return "South Africa’s Protection of Personal Information Act (POPIA)";
  if (region === "IN") return "applicable Indian data protection law";
  if (region === "JP") return "Japan’s Act on the Protection of Personal Information (APPI)";
  if (region === "BR") return "Brazil’s General Data Protection Law (LGPD)";
  return "applicable data protection and privacy laws in your jurisdiction";
}

export function buildDataProtectionPrintNotice(localeHint?: string | null): DataProtectionNotice {
  const law = resolveDataProtectionLaw(localeHint);
  return {
    title: "Data protection notice",
    law,
    body:
      `This verification report may contain personal data. Print, save, store, or share this PDF only where you have a lawful basis ` +
      `and in accordance with ${law}. Protect printed and electronic copies, do not leave them unattended, and dispose of them securely when no longer required.`,
  };
}

/**
 * Stronger wording for the full employee profile brochure, which aggregates far more personal data
 * (including health and incident records) than a single-section export.
 */
export function buildEmployeeBrochureDataProtectionNotice(localeHint?: string | null): DataProtectionNotice {
  const law = resolveDataProtectionLaw(localeHint);
  return {
    title: "Data protection notice",
    law,
    body:
      `This document is a complete employee record and contains personal data, and may contain special category data ` +
      `such as occupational health, medical and incident information. Produce, print, store, transmit, or share it only where you have ` +
      `a lawful basis and a demonstrable business need, and in accordance with ${law}. Keep printed and electronic copies secure, ` +
      `never leave them unattended, restrict onward disclosure to named recipients, and destroy them securely once no longer required.`,
  };
}
